
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import AOS from 'aos';

import { StaticDataActions, LocalActions } from './state';
import { ApplicationRole, Consent, UserProfile } from './core/models';
import { ProfileModalComponent } from './shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isPlatformBrowser } from '@angular/common';
import { SeoService } from './core/services';

export const DefaultMetaTags = {
  title: 'GiftShare – Solutia Simpla si Rapida pentru Cadoul Perfect',
  description: 'GiftShare este solutia rapida pentru a oferi un card cadou online. Alege un voucher cadou pentru cineva drag sau un gift card cadou ca beneficiu extrasalarial.',
  keywords: 'card cadou, voucher cadou, cadoul perfect, card cadou online, gift card cadou, card cadou universal, beneficii angajati, beneficii extrasalariale, platforma beneficii angajati',
  image: 'https://giftshare.ro/assets/images/resource/corporate-card.png',
};

@Component({
  selector: 'gs-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  readonly ApplicationRole = ApplicationRole;

  user: UserProfile;
  consent: Consent;
  canDisplayCookieBanner: boolean = false;

  private subscriptions: Subscription[] = [];

  get isPlatformBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      AOS.init();
    }

    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          const hasUserLoggedIn = !this.user && user;
          this.user = user;

          // Can this be moved to store?
          if (hasUserLoggedIn) {
            if (
              (!user.firstName ||
               !user.lastName ||
               !user.cityId) &&
               user.roles?.includes(ApplicationRole.Member) === true
            ) {
              this.modalService.open(ProfileModalComponent, { backdrop: 'static', keyboard: false });
            }
          }

        }),

      this.store.select<boolean>(state => state.local.canDisplayCookieBanner)
        .subscribe(canDisplayCookieBanner => {
          this.canDisplayCookieBanner = canDisplayCookieBanner;
        }),

      this.store.select<Consent>(state => state.local.consent)
        .subscribe(consent => {
          if (consent) {
            this.store.dispatch(new LocalActions.UpdateGoogleConsent(consent));
          }

          this.store.dispatch(new LocalActions.SetCanDisplayCookieBanner(!consent));
        })
    );

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.route),
      map(route => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      // filter(route => route.outlet === 'primary'),
      mergeMap(route => route.data))
      .subscribe((data) => {
        // Set static meta tags for SEO; dynamic tags are set in the page components (e.g. event-details.component.ts)

        // Title
        this.seoService.setTitle(data['title'] || DefaultMetaTags.title);

        // Description
        this.seoService.setDescription(data['description'] || DefaultMetaTags.description);

        // Keywords
        this.seoService.setKeywords(data['keywords'] || DefaultMetaTags.keywords);

        // Image
        this.seoService.setImage(data['image'] || DefaultMetaTags.image);

        // Canonical URL
        this.seoService.setUrl(data['url']);

      });

    // Refresh user data only on app reload; needed for backend triggered wallet updates
    // Moved to authState.ngxsOnInit
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}

